import React from 'react'

import PropTypes from 'prop-types'

import {Flex} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Component} from '@salesforce/commerce-sdk-react/components'

// Custom Context
import {useProductContext} from '../../../custom-contexts'

/**
 * PDPAdditionalBlocksGroup component renders a group of additional content blocks
 * for a product detail page (PDP) based on the product's additional content block IDs.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.regions - An array of region objects containing components.
 * @param {string} props.regions[].id - The ID of the region.
 * @param {Array} props.regions[].components - An array of component objects within the region.
 * @param {Object} props.regions[].components[].data - The data object for a component.
 * @param {string} props.regions[].components[].id - The ID of the component.
 * @param {string} props.regions[].components[].typeId - The type ID of the component.
 * @returns {JSX.Element|null} The rendered component or null if no components are available.
 */
export const PDPAdditionalBlocksGroup = ({regions}) => {
    // Use the context to get the product data and avoid API call
    // This also works in SSR
    const productData = useProductContext()
    let components = []
    if (regions) {
        components = regions[0].components || []
    }

    // Use the additional content block IDs from product data
    const idsArray = productData?.c_additionalContentBlockIDs || []

    // Filter out the components based on the IDs provided in product data
    const filteredComponents = components.filter((component) =>
        idsArray.includes(component.data.id)
    )

    let justifyContentValue = 'flex-start'
    if (filteredComponents.length == 2) {
        justifyContentValue = 'space-evenly'
    }
    else if (filteredComponents.length >= 3) {
        justifyContentValue = 'space-between'
    }

    return (
        <>
            {filteredComponents.length > 0 && (
                <Flex
                    justifyContent={justifyContentValue}
                    bg={'lightBeige'}
                    padding={{base: '10px', md: '40px', lg: '100px'}}
                    flexWrap={{base: 'wrap', md: '', lg: ''}}
                >
                    {filteredComponents.map((block) => (
                        <Component key={block.id} component={block} />
                    ))}
                </Flex>
            )}
        </>
    )
}

export default PDPAdditionalBlocksGroup

PDPAdditionalBlocksGroup.propTypes = {
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
